import React, { useState, useCallback } from "react";
import SearchBar from "../SearchBar/SearchBar";
import Pagination from "../Pagination/Pagination";
import { MODAL_MODES } from "../../../../constants/enums";
import "../css/generals.css";
import { useTranslation } from 'react-i18next';

const BaseTable = ({
  title,
  subtitle,
  items,
  loading,
  error,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  searchTerm,
  statusFilter,
  dateFilter,
  columns,
  renderRow,
  renderModal,
  renderAddModal,
  statusOptions,
  onLoadItems,
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  showDateFilter = false,
  DateFilterComponent = null
}) => {
  const { t } = useTranslation();

  /** -------------------- MODAL STATE -------------------- **/
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalMode, setModalMode] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const isModalOpen = !!selectedItem;

  const openModal = (item, mode) => {
    setSelectedItem(item);
    setModalMode(mode);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setModalMode("");
  };

  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const openViewModal = (item) => openModal(item, MODAL_MODES.VIEW);
  const openEditModal = (item) => openModal(item, MODAL_MODES.EDIT);
  const openDeleteModal = (item) => openModal(item, MODAL_MODES.DELETE);

  /** -------------------- EVENT HANDLERS -------------------- **/
  const handleSearch = useCallback((keyword, status) => {
    onLoadItems(1, pageSize, keyword, status, dateFilter);
  }, [pageSize, dateFilter, onLoadItems]);

  const handleDateChange = useCallback((newDate) => {
    onLoadItems(1, pageSize, searchTerm, statusFilter, newDate);
  }, [pageSize, searchTerm, statusFilter, onLoadItems]);

  const handlePageChange = useCallback((page) => {
    onLoadItems(page, pageSize, searchTerm, statusFilter, dateFilter);
  }, [pageSize, searchTerm, statusFilter, dateFilter, onLoadItems]);

  const changePageSizeHandler = useCallback((newSize) => {
    onLoadItems(1, newSize, searchTerm, statusFilter, dateFilter);
  }, [searchTerm, statusFilter, dateFilter, onLoadItems]);

  const saveItemHandler = async (updatedItem) => {
    try {
      await onUpdateItem(updatedItem);
      closeModal();
    } catch {
      alert(t('admin.base_table.update_failed', { title: title.toLowerCase() }));
    }
  };

  const confirmDeleteHandler = async () => {
    if (selectedItem) {
      await onDeleteItem(selectedItem);
      closeModal();
    }
  };

  const handleAddItemSuccess = useCallback((newItem) => {
    onAddItem(newItem);
  }, [onAddItem]);

  /** -------------------- RENDER -------------------- **/
  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <h3>{t('admin.base_table.loading')} {title.toLowerCase()}...</h3>
          <p>{t('admin.base_table.please_wait')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <h3>{t('admin.base_table.error_loading')} {title}</h3>
          <p>{error}</p>
          <button 
            className="btn btn-danger" 
            onClick={() => onLoadItems(currentPage, pageSize, searchTerm, statusFilter, dateFilter)}
          >
            {t('admin.base_table.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <button className="add-item-btn" onClick={openAddModal}>
          + {t('admin.base_table.add')} {title.slice(0, -1)}
        </button>
      </div>

      <div className="search-and-filters">
        <SearchBar
          options={statusOptions}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onSearch={handleSearch}
        />
        
        {showDateFilter && DateFilterComponent && (
          <DateFilterComponent
            onDateChange={handleDateChange}
            initialDate={dateFilter}
          />
        )}
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => renderRow(item, {
                onShowDetails: openViewModal,
                onEdit: openEditModal,
                onDelete: openDeleteModal
              }))
            ) : (
              <tr>
                <td colSpan={columns.length} className="empty-state">
                  <h3>{t('admin.base_table.no_items_found')}</h3>
                  <p>
                    {searchTerm || statusFilter || dateFilter
                      ? t('admin.base_table.adjust_filters') 
                      : t('admin.base_table.no_items_available')}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={handlePageChange}
          onItemsPerPageChange={changePageSizeHandler}
        />
      )}

      {isModalOpen && renderModal(selectedItem, modalMode, saveItemHandler, confirmDeleteHandler, closeModal)}

      {showAddModal && renderAddModal(closeAddModal, handleAddItemSuccess)}
    </div>
  );
};

export default BaseTable;